import { useCallback } from 'react';

import BoltIconSVG from '@assets/images/icons/legacy/bolt.svg';
import BedIconSVG from '@assets/images/icons/legacy/bed.svg';
import calculateMidColor from '@utilities/colors';
import {
  UpperMarker,
  LowerMarker,
  InnerMarker,
  MarketMarker,
  PortfolioVolatility,
  Gauge,
  InnerGauge,
  Bed,
  Bolt,
} from './styled';
import { useTheme } from '@simplywallst/ui-core';
import type { ComputedPortfolioHolding } from '@/components/DeprecatedPortfolio/redux/interface';

function getLowerMarkerColor(value: number, min: number, max: number) {
  const minMax = 0.1;
  const color = value * 0.002 - 0.1;
  const betaColor = calculateMidColor(
    min,
    max,
    Math.min(Math.max(-color, -minMax), minMax) * (0.5 / minMax) + 0.5,
    1
  );
  return { background: betaColor, borderBottomColor: betaColor };
}

function calculateBeta(value: number) {
  return Math.max(0, Math.min(value - 0.5, 1)) * 100;
}

interface Props {
  portfolioBeta?: number;
  portfolioName: string;
  holdings: ComputedPortfolioHolding[];
}
function VolatilityVsMarketChart(props: Props) {
  const theme = useTheme();
  const renderUpperMarker = useCallback(
    (portfolioBeta = 0, portfolioName: string) => {
      return (
        <UpperMarker style={{ left: calculateBeta(portfolioBeta) + '%' }}>
          <InnerMarker>
            <div>
              <abbr
                title={`${portfolioName} 5 year volatility relative to the market (Beta: ${
                  Math.round(portfolioBeta * 100) / 100
                })`}
              >
                <span>{portfolioName}</span>
              </abbr>
            </div>
          </InnerMarker>
        </UpperMarker>
      );
    },
    []
  );
  const renderLowerMarker = useCallback((item, count, min, max) => {
    return item.beta && item.currentValue ? (
      <LowerMarker
        key={count}
        style={{
          left: calculateBeta(item.beta) + '%',
          zIndex: Math.round(
            (4 - Math.max(0, Math.min(item.beta, 4), 0)) * 100
          ),
        }}
      >
        <InnerMarker>
          <div>
            <abbr
              title={`${
                item.tickerSymbol
              } 5 year volatility relative to the market (Beta: ${
                Math.round(item.beta * 100) / 100
              })`}
            >
              <span
                style={getLowerMarkerColor(calculateBeta(item.beta), min, max)}
              >
                {item.tickerSymbol}
              </span>
            </abbr>
          </div>
        </InnerMarker>
      </LowerMarker>
    ) : null;
  }, []);
  return (
    <PortfolioVolatility>
      <Gauge>
        <InnerGauge>
          {renderUpperMarker(props.portfolioBeta, props.portfolioName)}
          {props.holdings
            .filter((n) => !n.exclude)
            .map((item, count) =>
              renderLowerMarker(item, count, theme.color.bad, theme.color.good)
            )}
        </InnerGauge>
        <Bed>
          <BedIconSVG />
        </Bed>
        <Bolt>
          <BoltIconSVG />
        </Bolt>
      </Gauge>
      <MarketMarker>
        <InnerMarker>
          <div>
            <abbr title="Average volatility of market (Beta: 1, scale from 0.5 to 1.5)">
              <span style={{ borderBottomColor: '#D0CECE' }}>Market Avg</span>
            </abbr>
          </div>
        </InnerMarker>
      </MarketMarker>
    </PortfolioVolatility>
  );
}
export { VolatilityVsMarketChart };
